import { Data } from 'plotly.js';
import { isAnnotationValueSeries } from 'src/app/model/values-config';
import { ArrayUtils } from '../array-utils';
import { PlotDataWithIdentification } from '../input-converter/plotly-trace-generator';

export interface DetermineEarliestAndLatestOptions {

  /**
   * If true, annotation series will be excluded from determining the EARLIEST time.
   * This is useful for relative time mode where annotations should not
   * determine the base date.
   * Note: Annotations are still included when calculating the latest time
   * to ensure they remain visible on the graph.
   */
  excludeAnnotationSeriesFromEarliest?: boolean;
}

/**
 * Determine the earlierst and latest x-value dates from a list of
 * plot data entries.
 *
 * @param data Plot data entries
 * @param options Configuration options for the determination
 * @returns JS-object with earliest and latest date or `null` if no data is
 *    presentand no range could be specified.
 */
export function determineDataEarliestAndLatest(
  data: Data[],
  options: DetermineEarliestAndLatestOptions = {}
): { earliest: Date; latest: Date } | null {
  let earliest: Date | undefined;
  let latest: Date | undefined;
  const { excludeAnnotationSeriesFromEarliest } = options;

  for (const trace of data) {
    if (('x' in trace) === false) {
      continue;
    }

    const x = trace.x as Date[];
    if (x.length === 0) {
      continue;
    }

    // Check if this is an annotation series
    const plotDataTrace = trace as PlotDataWithIdentification;
    const isAnnotation = plotDataTrace.series && isAnnotationValueSeries(plotDataTrace.series);

    // Determine earliest date (skip annotation series if requested)
    if (!isAnnotation || !excludeAnnotationSeriesFromEarliest) {
      if (earliest === undefined) {
        earliest = x[0];
      } else {
        earliest = x[0] < earliest ? x[0] : earliest;
      }
    }

    // Determine latest date (always include all series)
    const end = ArrayUtils.endValue(x);
    if (latest === undefined) {
      latest = end;
    } else {
      latest = end > latest ? end : latest;
    }
  }

  if (earliest === undefined) {
    return null;
  }

  return { earliest, latest: latest! };
}
